import { Link } from 'react-router-dom'
import {ArrowLeft} from 'lucide-react'
import { useLang } from '@/context/LanguageContext'
import Seo from '@/components/Seo'

export default function LegalNotice() {
  const { t } = useLang()

  return (
    <div style={{ backgroundColor: '#F8F2EB', minHeight: '100vh' }}>
      <Seo
        title="Mentions légales — Clips'On"
        description="Éditeur, hébergeur et propriété intellectuelle du site clips-on.ch. Clips'ON Frédéric Hänni, entreprise individuelle, Genève, Suisse."
        path="/legal-notice"
      />
      {/* Header/Navigation */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 transition-colors"
          style={{ color: '#82391F', fontSize: '15px' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#E1593F')}
          onMouseLeave={e => (e.currentTarget.style.color = '#82391F')}
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          {t("Retour à l'accueil", "Back to home")}
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <h1 
          className="mb-16 tracking-[0.25em] uppercase"
          style={{ 
            color: '#0D0C0B',
            fontSize: '32px',
            fontWeight: 300,
            letterSpacing: '4px'
          }}
        >
          {t("Mentions légales", "Legal Notice")}
        </h1>

        <div className="space-y-12">
          {/* Éditeur du site */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Éditeur du site", "Website publisher")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">{t("Le site clips-on.ch est édité par :", "The website clips-on.ch is published by:")}</p>
              <p className="mb-1">Clips'ON Frédéric Hänni</p>
              <p className="mb-1">{t("Entreprise individuelle", "Sole proprietorship")}</p>
              <p className="mb-1">Avenue de Champel 28</p>
              <p className="mb-1">{t("1206 Genève, Suisse", "1206 Geneva, Switzerland")}</p>
              <p className="mb-1">{t("IDE : CHE-273.712.623", "Business identification number (UID): CHE-273.712.623")}</p>
              <p className="mb-1">{t("Inscrite au Registre du commerce de Genève le 7 juillet 2026", "Registered with the Geneva Commercial Registry on 7 July 2026")}</p>
              <p>{t("Contact : info@clips-on.ch", "Contact: info@clips-on.ch")}</p>
            </div>
          </section>

          {/* Hébergement */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Hébergement", "Hosting")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">{t("Le site est hébergé par :", "The website is hosted by:")}</p>
              <p className="mb-1">Infomaniak Network SA</p>
              <p className="mb-1">Rue Eugène-Marziano 25</p>
              <p className="mb-1">{t("1227 Les Acacias, Genève, Suisse", "1227 Les Acacias, Geneva, Switzerland")}</p>
              <p>www.infomaniak.com</p>
            </div>
          </section>

          {/* Propriété intellectuelle */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Propriété intellectuelle", "Intellectual property")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Le bouton Clips'ON est issu d'une technologie brevetée en 1995.",
                  "The Clips'ON button is based on technology patented in 1995."
                )}
              </p>
              <p>
                {t(
                  "L'ensemble du contenu du site clips-on.ch (textes, images, logos, éléments graphiques) est la propriété exclusive de Frédéric Haenni | Clips'ON, sauf mention contraire. Toute reproduction, représentation ou diffusion, en tout ou partie, du contenu de ce site sans autorisation préalable est interdite.",
                  "All content on clips-on.ch (text, images, logos, graphics) is the exclusive property of Frédéric Haenni | Clips'ON, unless otherwise stated. Any reproduction, representation, or distribution of this website's content, in whole or in part, without prior authorisation is prohibited."
                )}
              </p>
            </div>
          </section>

          {/* Données personnelles */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Données personnelles", "Personal data")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p>
                {t(
                  "Pour toute information relative à la collecte et au traitement de vos données personnelles, veuillez consulter notre ",
                  "For information about the collection and processing of your personal data, please refer to our "
                )}
                <Link 
                  to="/privacy-policy"
                  className="underline transition-colors"
                  style={{ color: '#E1593F' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                >
                  {t("Politique de confidentialité", "Privacy Policy")}
                </Link>
                .
              </p>
            </div>
          </section>

          {/* Crédits */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Crédits", "Credits")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-2">
                {t(
                  "Photographies produit : © Frédéric Haenni — retouches assistées par intelligence artificielle (OpenAI)",
                  "Product photography: \u00a9 Frédéric Haenni \u2014 AI-assisted retouching (OpenAI)"
                )}
              </p>
              <p>
                {t("Conception du site : Lumi.new", "Website design: Lumi.new")}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
